import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FlatList, Image, ImageSourcePropType, Pressable, StyleSheet, Text, useWindowDimensions, View } from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { ActionHeader } from '../components/AppHeader';
import BottomTabBar from '../components/BottomTabBar';
import colors from '../theme';
import { RootStackParamList } from '../navigation/RootNavigator';
import { Audiobook, getAudiobooks } from '../api/audiobooks';
import { getLibrary } from '../api/library';
import { narratorName } from '../utils/audiobookPeople';

type Props = NativeStackScreenProps<RootStackParamList, 'Downloads'>;

type DownloadItem = {
  id: string;
  audiobookId?: number;
  slug?: string;
  title: string;
  author: string;
  image: ImageSourcePropType;
};

const fallbackCovers = [
  require('../../assets/aural-continue-thumb.png'),
  require('../../assets/aural-book-1.png'),
  require('../../assets/aural-book-4.png'),
  require('../../assets/aural-book-5.png'),
  require('../../assets/aural-book-2.png'),
  require('../../assets/aural-bestseller-2.png'),
  require('../../assets/aural-bestseller-1.png'),
  require('../../assets/aural-bestseller-3.png'),
];

export default function DownloadsScreen({ navigation, route }: Props) {
  const { width } = useWindowDimensions();
  const params = route.params;
  const cardWidth = (width - 48 - 23) / 2;
  const [apiItems, setApiItems] = useState<DownloadItem[]>([]);
  const [loaded, setLoaded] = useState(false);
  const allItems = useMemo(() => apiItems, [apiItems]);
  const [visibleCount, setVisibleCount] = useState(16);
  const visibleItems = allItems.slice(0, visibleCount);
  const requestSeq = useRef(0);

  const loadMore = () => {
    setVisibleCount((count) => Math.min(count + 8, allItems.length));
  };

  const loadItems = useCallback(() => {
    const seq = requestSeq.current + 1;
    requestSeq.current = seq;
    setLoaded(false);
    setApiItems([]);
    setVisibleCount(16);
    const request = params?.libraryType
      ? getLibrary(params.libraryType)
      : getAudiobooks({
      per_page: 40,
      order_by: 'created_at',
      order: 'desc',
      topic_id: params?.topic_id,
      category_id: params?.category_id,
      person_id: params?.person_id,
    });

    request
      .then((items) => {
        if (requestSeq.current === seq) {
          setApiItems(items.map((item: Audiobook, index) => ({
            id: String(item.id),
            audiobookId: item.id,
            slug: item.slug,
            title: item.name,
            author: narratorName(item),
            image: item.cover ? { uri: item.cover } : fallbackCovers[index % fallbackCovers.length],
          })));
          setLoaded(true);
        }
      })
      .catch(() => {
        if (requestSeq.current === seq) {
          setApiItems([]);
          setLoaded(true);
        }
      });
  }, [params?.category_id, params?.libraryType, params?.person_id, params?.topic_id]);

  useEffect(() => {
    loadItems();
    const unsubscribe = navigation.addListener('focus', loadItems);

    return () => {
      requestSeq.current += 1;
      unsubscribe();
    };
  }, [loadItems, navigation]);

  return (
    <View style={styles.screen}>
      <FlatList
        data={visibleItems}
        keyExtractor={(item) => item.id}
        numColumns={2}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.container}
        columnWrapperStyle={styles.row}
        onEndReached={loadMore}
        onEndReachedThreshold={0.45}
        ListHeaderComponent={(
          <>
            <ActionHeader
              onBack={() => navigation.goBack()}
              rightIcons={['search-outline', 'ellipsis-horizontal']}
              onRightPress={[() => navigation.navigate('GetSuggestions')]}
            />

            <Text style={styles.title}>{params?.title ?? 'Danh sách'}</Text>
            <Text style={styles.subtitle}>{loaded ? `${apiItems.length} sách nói` : 'Đang tải dữ liệu'}</Text>
          </>
        )}
        renderItem={({ item }) => (
          <Pressable style={{ width: cardWidth }} onPress={() => navigation.navigate('BookOverview', { id: item.audiobookId, slug: item.slug })}>
            <Image source={item.image} style={styles.cover} resizeMode="cover" />
            <Text style={styles.bookTitle} numberOfLines={2}>{item.title}</Text>
            <Text style={styles.author} numberOfLines={1}>{item.author}</Text>
          </Pressable>
        )}
        ListEmptyComponent={loaded ? (
          <View style={styles.emptyState}>
            <Text style={styles.emptyTitle}>Chưa có sách nói</Text>
            <Text style={styles.emptyText}>Dữ liệu sẽ hiển thị khi API trả về danh sách.</Text>
          </View>
        ) : (
          <View style={styles.skeletonGrid}>
            {[0, 1, 2, 3, 4, 5].map((item) => (
              <View key={item} style={{ width: cardWidth, marginBottom: 28 }}>
                <View style={styles.skeletonCover} />
                <View style={styles.skeletonTitle} />
                <View style={styles.skeletonAuthor} />
              </View>
            ))}
          </View>
        )}
      />

      <BottomTabBar
        active="My Library"
        onHome={() => navigation.navigate('Home')}
        onDiscover={() => navigation.navigate('GetSuggestions')}
        onLibrary={() => navigation.navigate('Library')}
        onProfile={() => navigation.navigate('Settings')}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: 'rgb(242, 249, 253)',
  },
  container: {
    paddingTop: 62,
    paddingHorizontal: 24,
    paddingBottom: 34,
  },
  title: {
    color: colors.textPrimary,
    fontSize: 24,
    lineHeight: 32,
    fontWeight: '600',
    marginBottom: 9,
  },
  subtitle: {
    color: colors.textSecondary,
    fontSize: 12,
    lineHeight: 16,
    fontWeight: '500',
    marginBottom: 25,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 28,
  },
  cover: {
    width: '100%',
    aspectRatio: 1,
    borderRadius: 8,
    backgroundColor: colors.card,
    marginBottom: 16,
  },
  bookTitle: {
    color: colors.textPrimary,
    fontSize: 16,
    lineHeight: 20,
    fontWeight: '600',
    marginBottom: 4,
  },
  author: {
    color: colors.textSecondary,
    fontSize: 12,
    lineHeight: 16,
    fontWeight: '500',
  },
  skeletonGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  skeletonCover: {
    width: '100%',
    aspectRatio: 1,
    borderRadius: 8,
    backgroundColor: '#ECEEF2',
    marginBottom: 16,
  },
  skeletonTitle: {
    width: '86%',
    height: 16,
    borderRadius: 6,
    backgroundColor: '#ECEEF2',
    marginBottom: 8,
  },
  skeletonAuthor: {
    width: '58%',
    height: 12,
    borderRadius: 6,
    backgroundColor: '#ECEEF2',
  },
  emptyState: {
    paddingVertical: 48,
    alignItems: 'center',
  },
  emptyTitle: {
    color: colors.textPrimary,
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 8,
  },
  emptyText: {
    color: colors.textSecondary,
    fontSize: 13,
    textAlign: 'center',
  },
});
